import { ChangeDetectorRef, Component, DestroyRef, HostBinding, Input, OnChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CompetitionBatchRequest, VirtualCompetitionStatisticsRequest } from '@cds/query-objects';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { GridGrouping, LeagueGroup, StandingsData } from '@frontend/sports/grid/core/feature/model';
import { produce } from 'immer';
import { defer, map } from 'rxjs';

import { StatisticsApiService } from '../../statistics/statistics-api.service';

@Component({
    selector: 'ms-standings',
    templateUrl: 'standings.component.html',
})
export class StandingsComponent implements OnChanges {
    @Input() standingsData: StandingsData;
    @Input() sport: CountItem;
    @Input() hideFavourites = false;
    @HostBinding('class') className = 'standings';

    groups: LeagueGroup[];
    grouping = GridGrouping.League;
    private readonly destroyRef = inject(DestroyRef);

    leagueStatisticsComponent$ = defer(() => import('../../statistics/league-statistics.component')).pipe(map((c) => c.LeagueStatisticsComponent));

    constructor(
        private cdsStatsService: StatisticsApiService,
        private changeRef: ChangeDetectorRef,
    ) {}

    ngOnChanges(): void {
        if (this.sport.id !== SportConstant.Soccer) {
            return;
        }

        this.addStatisticsToGroups();
    }

    groupToggled(group: LeagueGroup): void {
        this.groups = produce(this.groups, (draft) => {
            const local = draft.find((current) => current.id === group.id);

            if (local) {
                local.collapsed = !local.collapsed;
            }
        });
    }

    trackGroup(index: number, group: LeagueGroup): number {
        return group.id;
    }

    private addStatisticsToGroups(): void {
        const competitionBatchRequest = {
            sportId: this.sport.id,
            competitionIds: this.getCompetitionsIds(),
            virtualCompetitionStatisticsRequest: this.getVirtualCompetitionsStatistics(),
        } as CompetitionBatchRequest;

        if (competitionBatchRequest.competitionIds.length || competitionBatchRequest.virtualCompetitionStatisticsRequest.length) {
            this.cdsStatsService
                .getCompetitionsBatchStatistics(competitionBatchRequest)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((statistics) => {
                    this.groups = this.standingsData.groups.map((standingGroup) => ({
                        ...standingGroup,
                        statistics: statistics.find((s) => s.competitionId === standingGroup.id || s.groupId === standingGroup.id),
                    }));
                    this.changeRef.detectChanges();
                });
        }
    }

    private getCompetitionsIds(): number[] {
        return this.standingsData.groups.filter((group) => !group.virtualCompetitionId || !group.virtualCompetitionGroupId).map((group) => group.id);
    }

    private getVirtualCompetitionsStatistics(): VirtualCompetitionStatisticsRequest[] {
        return this.standingsData.groups
            .filter((group) => group.virtualCompetitionId && group.virtualCompetitionGroupId)
            .reduce<VirtualCompetitionStatisticsRequest[]>((result, group) => {
                const virtualCompetition = result.find((x) => x.virtualCompetitionId === group.virtualCompetitionId);

                if (virtualCompetition) {
                    virtualCompetition.virtualCompetitionGroupIds.push(group.virtualCompetitionGroupId!);
                } else {
                    result.push({
                        virtualCompetitionId: group.virtualCompetitionId!,
                        virtualCompetitionGroupIds: [group.virtualCompetitionGroupId!],
                    } as VirtualCompetitionStatisticsRequest);
                }

                return result;
            }, [] as VirtualCompetitionStatisticsRequest[]);
    }
}
